import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Button,
  Space,
  message,
  Typography
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  ApartmentOutlined,
  DownloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import groupRelationshipService from '../services/groupRelationshipService';
import EmployeeSelect from '../components/common/EmployeeSelect';
import weworkService from '../services/weworkService';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const GroupRelationshipAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('day'),
    dayjs().endOf('day')
  ]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedEmployeeUserids, setSelectedEmployeeUserids] = useState([]);
  const [employees, setEmployees] = useState([]);

  // 概览数据
  const [overviewData, setOverviewData] = useState({
    totalGroups: 0,
    totalMembers: 0,
    newMembers: 0,
    lostMembers: 0
  });

  // 时间快捷选项
  const rangePresets = [
    {
      label: '今天',
      value: [dayjs().startOf('day'), dayjs().endOf('day')],
    },
    {
      label: '昨天',
      value: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
    },
    {
      label: '近3天',
      value: [dayjs().subtract(2, 'days').startOf('day'), dayjs().endOf('day')],
    },
    {
      label: '最近1个月',
      value: [dayjs().subtract(1, 'month').startOf('day'), dayjs().endOf('day')],
    },
    {
      label: '最近3个月',
      value: [dayjs().subtract(3, 'months').startOf('day'), dayjs().endOf('day')],
    },
    {
      label: '最近1年',
      value: [dayjs().subtract(1, 'year').startOf('day'), dayjs().endOf('day')],
    },
  ];

  // 加载员工列表并建立ID到userid的映射
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeeList = await weworkService.getAllEmployees();
        setEmployees(employeeList);
      } catch (error) {
        console.error('获取员工列表失败:', error);
      }
    };
    fetchEmployees();
  }, []);

  // 加载数据
  const fetchData = async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0]?.format('YYYY-MM-DD');
      const endDate = dateRange[1]?.format('YYYY-MM-DD');

      // 获取所有数据（不传userid参数）
      const result = await groupRelationshipService.getGroupOverview(
        startDate,
        endDate,
        null  // 不传userid，获取所有群主数据
      );

      // 前端按多选的群主筛选
      let filteredData = result;
      if (selectedEmployeeUserids && selectedEmployeeUserids.length > 0) {
        filteredData = result.filter(item =>
          selectedEmployeeUserids.includes(item.ownerUserid)
        );
      }

      // 处理数据
      const processedData = filteredData.map((item, index) => ({
        key: index,
        ...item
      }));

      setData(processedData);

      // 计算概览数据
      const overview = {
        totalGroups: filteredData.reduce((sum, item) => sum + (item.groupCount || 0), 0),
        totalMembers: filteredData.reduce((sum, item) => sum + (item.totalMembers || 0), 0),
        newMembers: filteredData.reduce((sum, item) => sum + (item.newMembers || 0), 0),
        lostMembers: filteredData.reduce((sum, item) => sum + (item.lostMembers || 0), 0)
      };

      setOverviewData(overview);
    } catch (error) {
      console.error('获取数据失败:', error);
      message.error('获取数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 处理员工选择变化（多选）
  const handleEmployeeChange = (values) => {
    setSelectedEmployees(values);

    if (values && values.length > 0) {
      // 将员工ID数组转换为userid数组
      const userids = values.map(id => {
        const employee = employees.find(emp => String(emp.id) === String(id));
        return employee ? employee.userid : null;
      }).filter(userid => userid !== null);
      setSelectedEmployeeUserids(userids);
    } else {
      setSelectedEmployeeUserids([]);
    }
  };

  // 处理查询
  const handleQuery = () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning('请选择日期范围');
      return;
    }
    fetchData();
  };

  // 重置筛选
  const handleReset = () => {
    setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
    setSelectedEmployees([]);
    setSelectedEmployeeUserids([]);
    // 重置后重新加载数据
    setTimeout(() => fetchData(), 100);
  };

  // 导出Excel
  const handleExport = () => {
    if (data.length === 0) {
      message.warning('暂无数据可导出');
      return;
    }

    try {
      // 准备导出数据
      const exportData = data.map(item => ({
        '群主姓名': item.ownerName || '-',
        '部门': item.departmentName || '-',
        '群数量': item.groupCount || 0,
        '总客户数': item.totalMembers || 0,
        '新增客户数': item.newMembers || 0,
        '流失客户数': item.lostMembers || 0
      }));

      // 创建CSV内容
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => headers.map(header => row[header]).join(','))
      ].join('\n');

      // 添加BOM以支持Excel正确显示中文
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

      // 创建下载链接
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `企微社群分析_${dateRange[0].format('YYYY-MM-DD')}_${dateRange[1].format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请稍后重试');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '群主姓名',
      dataIndex: 'ownerName',
      key: 'ownerName',
      fixed: 'left',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '部门',
      dataIndex: 'departmentName',
      key: 'departmentName',
      width: 150,
      render: (text) => text || '-'
    },
    {
      title: '群数量',
      dataIndex: 'groupCount',
      key: 'groupCount',
      width: 100,
      sorter: (a, b) => (a.groupCount || 0) - (b.groupCount || 0),
      render: (text) => text || 0
    },
    {
      title: '总客户数',
      dataIndex: 'totalMembers',
      key: 'totalMembers',
      width: 120,
      sorter: (a, b) => (a.totalMembers || 0) - (b.totalMembers || 0),
      render: (text) => text || 0
    },
    {
      title: '新增客户数',
      dataIndex: 'newMembers',
      key: 'newMembers',
      width: 120,
      sorter: (a, b) => (a.newMembers || 0) - (b.newMembers || 0),
      render: (text) => text || 0
    },
    {
      title: '流失客户数',
      dataIndex: 'lostMembers',
      key: 'lostMembers',
      width: 120,
      sorter: (a, b) => (a.lostMembers || 0) - (b.lostMembers || 0),
      render: (text) => text || 0
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>企微社群分析</Title>
        </Col>
      </Row>

      {/* 筛选区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" wrap>
          <span>时间范围:</span>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="YYYY-MM-DD HH:mm:ss"
            showTime
            presets={rangePresets}
            style={{ width: 400 }}
          />
          <span>群主筛选:</span>
          <EmployeeSelect
            multiple
            value={selectedEmployees}
            onChange={handleEmployeeChange}
            placeholder="选择群主（默认全部）"
            allowClear
            style={{ width: 300 }}
            showDisplayModeSwitch={false}
            defaultDisplayMode="department"
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleQuery}
            loading={loading}
          >
            查询
          </Button>
          <Button onClick={handleReset}>
            重置
          </Button>
        </Space>
      </Card>

      {/* 数据概览卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="企微社群总数"
              value={overviewData.totalGroups}
              prefix={<ApartmentOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="企微社群客户总数"
              value={overviewData.totalMembers}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="企微社群新增客户数"
              value={overviewData.newMembers}
              prefix={<UserAddOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="企微社群流失客户数"
              value={overviewData.lostMembers}
              prefix={<UserDeleteOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 数据明细表格 */}
      <Card
        title="数据明细"
        extra={
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={data.length === 0}
          >
            导出Excel
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            defaultPageSize: 20,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default GroupRelationshipAnalysis;
